const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function getMonthName (year, month) {
  return MONTH_NAMES[month]
}

export function getMonthLabel (year, month) {
  return `${MONTH_NAMES[month]} ${year}`
}

export function getDayNames () {
  return DAY_NAMES
}

export function getMonthDays (year, month) {
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const daysInPrevMonth = new Date(year, month, 0).getDate()
  const days = []

  // Normalize previous month (handles January -> December of previous year)
  const prevMonthDate = new Date(year, month - 1, 1)
  const prevYear = prevMonthDate.getFullYear()
  const prevMonth = prevMonthDate.getMonth()

  // Pad start with previous month days
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({ day: daysInPrevMonth - i, month: prevMonth, year: prevYear, isOtherMonth: true })
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ day: d, month, year, isOtherMonth: false })
  }

  // Normalize next month (handles December -> January of next year)
  const nextMonthDate = new Date(year, month + 1, 1)
  const nextYear = nextMonthDate.getFullYear()
  const nextMonth = nextMonthDate.getMonth()

  // Pad end to complete 6 rows (42 cells)
  const remaining = 42 - days.length
  for (let d = 1; d <= remaining; d++) {
    days.push({ day: d, month: nextMonth, year: nextYear, isOtherMonth: true })
  }

  return days
}

export function isToday (year, month, day) {
  const t = new Date()
  return t.getFullYear() === year && t.getMonth() === month && t.getDate() === day
}

export function formatDate (year, month, day) {
  const m = String(month + 1).padStart(2, '0')
  const d = String(day).padStart(2, '0')
  return `${year}-${m}-${d}`
}

const _DAY_MS = 86400000

// Greedy lane packing — assigns each event a `_lane` (0-indexed column)
// and `_lanes` (total columns its overlap cluster needs). Used by the week
// view to render side-by-side overlapping events without them stacking on
// top of each other. O(n²) but n is small (events on one day).
const _toMins = (t) => {
  const [h, m] = (t || '00:00').split(':').map(Number)
  return h * 60 + m
}
export function assignLanes (evs) {
  if (!evs.length) return evs
  // Shallow-clone every event so we don't mutate the canonical events array.
  const sorted = evs.map(e => ({ ...e })).sort((a, b) => _toMins(a.startTime) - _toMins(b.startTime))
  // Group into overlap clusters: events that transitively overlap end up in
  // the same cluster and share the same lane count.
  const clusters = []
  let cur = null
  let curEnd = -1
  for (const ev of sorted) {
    const s = _toMins(ev.startTime)
    const e = _toMins(ev.endTime)
    if (cur && s < curEnd) {
      cur.push(ev)
      if (e > curEnd) curEnd = e
    } else {
      cur = [ev]
      clusters.push(cur)
      curEnd = e
    }
  }
  for (const cluster of clusters) {
    // Assign lanes within the cluster — pack each event into the lowest lane
    // whose last event has already ended.
    const laneEnds = []
    for (const ev of cluster) {
      const s = _toMins(ev.startTime)
      const e = _toMins(ev.endTime)
      let lane = laneEnds.findIndex(end => end <= s)
      if (lane === -1) {
        lane = laneEnds.length
        laneEnds.push(e)
      } else {
        laneEnds[lane] = e
      }
      ev._lane = lane
    }
    const total = laneEnds.length
    for (const ev of cluster) ev._lanes = total
  }
  return sorted
}

// Cross-midnight tail: an event with `endTime <= startTime` actually runs
// past midnight into the next day. The start day shows the [startTime, 24:00]
// portion (clipped via eventToHeight); this helper produces a synthetic
// [00:00, endTime] piece for the next day.
const _isCrossMidnight = (ev) => {
  if (!ev?.startTime || !ev?.endTime) return false
  const [sh, sm] = ev.startTime.split(':').map(Number)
  const [eh, em] = ev.endTime.split(':').map(Number)
  return (eh * 60 + em) <= (sh * 60 + sm)
}

export function getEventsForDay (events, year, month, day) {
  const dateStr = formatDate(year, month, day)
  const target = new Date(year, month, day)
  const targetDow = target.getDay()
  const prevDate = formatDate(year, month, day - 1) // JS Date normalizes day=0/-1

  const out = []
  for (const ev of (events || [])) {
    // Cross-midnight tail from yesterday → render as a [00:00, endTime] piece.
    if (ev.date === prevDate && _isCrossMidnight(ev)) {
      out.push({ ...ev, startTime: '00:00', _isCrossTail: true })
      continue
    }
    if (ev.date === dateStr) { out.push(ev); continue }
    if (!ev.recurring || ev.recurring === 'none') continue
    const [oy, om, od] = ev.date.split('-').map(Number)
    if (!oy) continue
    const orig = new Date(oy, om - 1, od)
    if (target < orig) continue
    let match = false
    switch (ev.recurring) {
      case 'daily': match = true; break
      case 'weekdays': match = targetDow >= 1 && targetDow <= 5; break
      case 'weekends': match = targetDow === 0 || targetDow === 6; break
      case 'weekly': {
        const days = ev.recurringDays
        match = Array.isArray(days) && days.length
          ? days.includes(targetDow)
          : orig.getDay() === targetDow
        break
      }
      case 'biweekly': {
        if (orig.getDay() !== targetDow) break
        const diffDays = Math.round((target - orig) / _DAY_MS)
        match = diffDays % 14 === 0
        break
      }
      case 'monthly': match = orig.getDate() === target.getDate(); break
      case 'yearly': match = orig.getMonth() === target.getMonth() && orig.getDate() === target.getDate(); break
    }
    if (match) out.push(ev)
  }
  return out
}

// ─── Time helpers (shared by all views) ──────────────────────────────────

export const HOUR_PX = 56
export const SLOT_MIN = 15

// Intl.DateTimeFormat is expensive to construct (one of the slowest things
// in JS). Cache one per timezone so the hot loops in availability/header
// renders don't allocate hundreds per minute tick.
const _timeFmt = new Map()
const _dateFmt = new Map()

export const formatTzTime = (ms, tz) => {
  let f = _timeFmt.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false
    })
    _timeFmt.set(tz, f)
  }
  return f.format(ms)
}

export const formatTzDate = (ms, tz) => {
  let f = _dateFmt.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit'
    })
    _dateFmt.set(tz, f)
  }
  // en-CA returns YYYY-MM-DD already
  return f.format(ms).replace(/\u2010|\u2013/g, '-')
}

export const hhmmToMin = (s) => {
  if (!s) return 0
  const [h, m] = s.split(':').map(Number)
  return h * 60 + m
}

export const minToHHMM = (n) => {
  const total = Math.max(0, Math.min(24 * 60, Math.round(n)))
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export const eventUtcMs = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return 0
  const [y, m, d] = dateStr.split('-').map(Number)
  const [h, mm] = timeStr.split(':').map(Number)
  return new Date(y, m - 1, d, h, mm).getTime()
}

// Backwards-compat aliases (private, so existing internal usages don't need a sweep)
const _formatTzTime = formatTzTime
const _formatTzDate = formatTzDate
const _hhmmToMin = hhmmToMin

// Returns null (available), or one of 'static' / 'event' for unavailability.
//   'static' = outside working hours (general off-hours, configured in profile)
//   'event'  = busy/OOO event from the events array (custom, ad-hoc)
//
// Events with type='busy' (intra-day, has startTime/endTime) and type='ooo'
// (full-day, may have endDate) on the events list are read inline so the
// helper has a single source of truth shared by every view.
function _availabilityType (ms, member, events) {
  if (!member?.timezone) return null
  const tz = member.timezone
  const localDate = _formatTzDate(ms, tz)
  const localTime = _formatTzTime(ms, tz)
  const cur = _hhmmToMin(localTime)

  // Event-type: busy events from the events array. Busy + OOO are merged
  // into a single 'busy' type. If endDate is set (or differs from date),
  // it's a multi-day full-day block. Otherwise it's an intra-day time range.
  for (const ev of (events || [])) {
    if (ev.type !== 'busy') continue
    if (!(ev.invitees || []).includes(member.email)) continue
    const isMultiDay = ev.endDate && ev.endDate !== ev.date
    if (isMultiDay) {
      if (localDate >= ev.date && localDate <= ev.endDate) return 'event'
    } else {
      if (ev.date !== localDate) continue
      const s = _hhmmToMin(ev.startTime || '00:00')
      const e = _hhmmToMin(ev.endTime || '24:00')
      if (cur >= s && cur < e) return 'event'
    }
  }

  // Static: outside working hours (handles midnight-spanning ranges where end < start)
  const wh = member.workingHours
  if (wh) {
    const start = _hhmmToMin(wh.start)
    const end = _hhmmToMin(wh.end)
    const inHours = start < end
      ? (cur >= start && cur < end)
      : (cur >= start || cur < end) // wraps midnight
    if (!inHours) return 'static'
  }

  return null
}

/**
 * Walks `totalMinutes` from `baseMs` in `slotMinutes` steps, grouping
 * consecutive same-type unavailable slots into ranges.
 *
 * @param {Object} member         person record with timezone/workingHours
 * @param {number} baseMs         UTC ms timestamp at minute 0 of the window
 * @param {number} totalMinutes   window length (default 24*60)
 * @param {number} slotMinutes    step size (default 15)
 * @param {Array}  events         events list (busy/ooo entries are read here)
 * @returns {Array<{startMin, endMin, type}>}
 */
export function getUnavailableRanges (member, baseMs, totalMinutes = 24 * 60, slotMinutes = 15, events = []) {
  if (!member) return []
  // Day columns can render before their `baseMs` reactive prop resolves
  // (state still rehydrating). Skip those frames — Intl.DateTimeFormat
  // .format(NaN) throws "Invalid time value" and would tear down every
  // DayColumn in the smbls 3.14 week view.
  if (!Number.isFinite(baseMs)) return []
  const STEP_MS = slotMinutes * 60 * 1000
  const ranges = []
  let curStart = null
  let curType = null
  const steps = Math.ceil(totalMinutes / slotMinutes)
  for (let i = 0; i <= steps; i++) {
    const isLast = i >= steps
    let type = null
    if (!isLast) {
      type = _availabilityType(baseMs + i * STEP_MS, member, events)
    }
    if (type !== curType || isLast) {
      if (curType != null && curStart != null) {
        ranges.push({ startMin: curStart, endMin: i * slotMinutes, type: curType })
      }
      curStart = type ? i * slotMinutes : null
      curType = type
    }
  }
  return ranges
}

// Returns true if the calendar week containing (year, month, day) also
// contains the user's local "today". Used to gate the NowLine indicator
// in the week view.
export function weekContainsToday (year, month, day) {
  const t = new Date()
  const ty = t.getFullYear()
  const tm = t.getMonth()
  const td = t.getDate()
  const ref = new Date(year, month, day)
  const dow = ref.getDay()
  const start = new Date(year, month, day - dow)
  const end = new Date(year, month, day - dow + 6)
  const today = new Date(ty, tm, td)
  return today >= start && today <= end
}

export function getWeekDays (year, month, day) {
  const d = new Date(year, month, day)
  const dow = d.getDay()
  const days = []
  for (let i = 0; i < 7; i++) {
    const wd = new Date(year, month, day - dow + i)
    days.push({
      year: wd.getFullYear(),
      month: wd.getMonth(),
      day: wd.getDate(),
      dayName: DAY_NAMES[i]
    })
  }
  return days
}

export function getHours () {
  const hours = []
  for (let h = 0; h < 24; h++) {
    const label = h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`
    hours.push({ h, label })
  }
  return hours
}

// Role helpers — re-exported from shared-gov so the canonical
// implementation lives in one place. Calendar's components still
// import from `calendarUtils.js` so we keep these as transparent
// re-exports.
export {
  hasRole,
  userIsAdmin,
  userIsSuperadmin,
  isExternalEmail,
  currentUser,
  currentUserIsAdmin,
  currentUserIsSuperadmin
} from '../roleHelpers.js'

import {
  hasRole as _hasRole,
  isExternalEmail as _isExternalEmail
} from '../roleHelpers.js'

// Should this event be marked private (i.e. hidden as "Unavailable"
// from non-creators) at SAVE time? Calendar-specific policy — kept
// here because it references event-shape fields. True when:
//   - Creator is a superadmin AND it isn't a Company event, OR
//   - Creator is external to the company team
export function shouldStampPrivate (creator, type, people) {
  if (!creator) return false
  const isExt = _isExternalEmail(creator.email || creator, people)
  if (isExt) return true
  if (type === 'general') return false
  return _hasRole(creator, 'superadmin')
}

// Should this event be hidden as "Unavailable" from the current viewer?
// Private events:
//   - The viewer is not the creator AND
//   - The viewer is not a superadmin AND
//   - The event is flagged isPrivate
// Returns true → render as a generic Unavailable block, no details exposed.
export function eventIsPrivateFor (event, viewer) {
  if (!event?.isPrivate) return false
  // Superadmin (the only role allowed to do this for now) can publish a
  // private event to the team via `isTeamShared`. Once it's shared, the
  // event renders normally for everyone.
  if (event.isTeamShared) return false
  if (!viewer) return true
  if (viewer.email === event.creatorEmail) return false
  if (_hasRole(viewer, 'superadmin')) return false
  return true
}

// Infer the recurrence pattern from sibling events sharing the same base
// google_event_id. Google Calendar recurring events encode the instance date
// as a suffix (e.g. `baseId_20260413T193000Z`), so all instances of the
// same series share a common prefix. We analyse the loaded siblings' dates
// to determine daily / weekly / biweekly / monthly patterns.
export function inferRecurrence (events, event) {
  // If the event already has explicit recurrence info, use it.
  if (event?.recurring && event.recurring !== 'none') {
    return { recurring: event.recurring, recurringDays: event.recurringDays || [] }
  }
  if (!event?.googleEventId) return { recurring: 'none', recurringDays: [] }

  const baseId = _baseEventId(event.googleEventId)
  // No date suffix → not a recurring instance
  if (!baseId || baseId === event.googleEventId) {
    return { recurring: 'none', recurringDays: [] }
  }

  const siblings = (events || []).filter(e =>
    _baseEventId(e.googleEventId) === baseId
  )
  if (siblings.length < 2) return { recurring: 'none', recurringDays: [] }

  // Unique sorted dates and their day-of-week
  const dateSet = new Set(siblings.map(e => e.date))
  const dates = [...dateSet].sort()
  const parsed = dates.map(d => {
    const [y, m, day] = d.split('-').map(Number)
    return new Date(y, m - 1, day)
  })
  const dows = [...new Set(parsed.map(d => d.getDay()))]

  // Consecutive intervals in days
  const intervals = []
  for (let i = 1; i < parsed.length; i++) {
    intervals.push(Math.round((parsed[i] - parsed[i - 1]) / _DAY_MS))
  }
  if (!intervals.length) return { recurring: 'none', recurringDays: [] }

  // Pattern detection
  if (intervals.every(i => i === 1)) return { recurring: 'daily', recurringDays: [] }
  if (intervals.every(i => i === 7) && dows.length === 1) return { recurring: 'weekly', recurringDays: dows }
  if (intervals.every(i => i === 14) && dows.length === 1) return { recurring: 'biweekly', recurringDays: [] }

  // Same day-of-month across months
  const doms = [...new Set(parsed.map(d => d.getDate()))]
  if (doms.length === 1 && intervals.every(i => i >= 28 && i <= 31)) {
    return { recurring: 'monthly', recurringDays: [] }
  }

  // Weekday-only pattern (Mon–Fri)
  const allWeekdays = dows.every(d => d >= 1 && d <= 5)
  if (allWeekdays && dows.length === 5) return { recurring: 'weekdays', recurringDays: [] }

  // Multiple days per week — treat as weekly with specific days
  if (dows.length >= 1 && dows.length <= 5) {
    return { recurring: 'weekly', recurringDays: dows }
  }

  return { recurring: 'weekly', recurringDays: dows }
}

// Fresh newBooking payload — merges any caller-provided overrides on top of
// a clean baseline so previously-typed fields (description, meetUrl, etc.)
// don't leak between modal opens.
export function freshBooking (overrides = {}) {
  return {
    title: '',
    date: '',
    startTime: '09:00',
    endTime: '10:00',
    allDay: false,
    invitees: '',
    inviteeQuery: '',
    description: '',
    type: 'meeting',
    meetingMode: 'symbols-meet',
    meetUrl: '',
    recurring: 'none',
    recurringDays: [],
    editingEventId: null,
    ...overrides
  }
}

export function eventToTop (startTime) {
  const [h, m] = startTime.split(':').map(Number)
  return (h * 60 + m) / 60 * 56
}

export function eventToHeight (startTime, endTime) {
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  const startMins = sh * 60 + sm
  // Cross-midnight: clip to end-of-day (24:00). The next-day tail is rendered
  // separately by getEventsForDay's wrap-around branch.
  let endMins = eh * 60 + em
  if (endMins <= startMins) endMins = 24 * 60
  return Math.max((endMins - startMins) / 60 * 56, 24)
}

// Convert pixel offset within the time grid into HH:MM, snapped to 15-min.
export function pxToTime (px) {
  const totalMinutes = Math.max(0, Math.min(24 * 60 - 15, Math.round(px / 56 * 60 / 15) * 15))
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Add `mins` minutes to an HH:MM string.
export function addMinutes (time, mins) {
  const [h, m] = time.split(':').map(Number)
  const total = Math.min(24 * 60, h * 60 + m + mins)
  const nh = Math.floor(total / 60)
  const nm = total % 60
  return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`
}

export function getEventTheme (color) {
  const map = {
    blue: 'eventBlue',
    green: 'eventGreen',
    red: 'eventRed',
    purple: 'eventPurple',
    orange: 'eventOrange',
    indigo: 'eventIndigo'
  }
  return map[color] || 'eventBlue'
}

export function prevMonth (year, month) {
  if (month === 0) return { year: year - 1, month: 11 }
  return { year, month: month - 1 }
}

export function nextMonth (year, month) {
  if (month === 11) return { year: year + 1, month: 0 }
  return { year, month: month + 1 }
}

// Lazy-load `description` and `invitees` for a single event when its
// detail modal opens. The initial calendar fetch skips these fields
// (they're 68% of the payload) so the grid loads fast. Mutates the row
// in `root.events` so any view referencing the same id sees the new fields.
import { getSDK } from '../../../shared/functions/sdk.js'
const _detailCache = new Set()

// Extract the base recurring event ID (strip the date suffix).
// e.g. "09aql32kefq530bfigjd8gvarc_20260413T193000Z" → "09aql32kefq530bfigjd8gvarc"
const _baseEventId = (geid) => geid ? geid.replace(/_\d{8}T\d{6}Z$/, '') : null

export async function fetchEventDetails (eventId, root) {
  if (!eventId || _detailCache.has(eventId)) return
  _detailCache.add(eventId)
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.calendar get
    const result = await getSDK().execute('workspaceProject.calendar', 'get', {
      id: eventId,
      options: { columns: 'id,description,invitees' }
    })
    const data = Array.isArray(result?.data)
      ? result.data[0]
      : (result?.data ?? result ?? null)
    if (!data) return
    let invitees = data.invitees || []
    let description = data.description

    // If this instance has no invitees, inherit from a sibling (same recurring series)
    if (!invitees.length) {
      const ev = (root.events || []).find(e => e.id === eventId)
      const baseId = _baseEventId(ev?.googleEventId)
      if (baseId) {
        const sibling = (root.events || []).find(e =>
          e.id !== eventId &&
          e.invitees?.length &&
          _baseEventId(e.googleEventId) === baseId
        )
        if (sibling) {
          invitees = sibling.invitees
          if (!description && sibling.description) description = sibling.description
        }
      }
    }

    const events = root.events || []
    const next = events.map(e => e.id === eventId
      ? { ...e, description, invitees }
      : e
    )
    const updates = { events: next }
    if (root.selectedEvent?.id === eventId) {
      updates.selectedEvent = { ...root.selectedEvent, description, invitees }
    }
    root.update(updates)
  } catch (e) {
    _detailCache.delete(eventId)
  }
}
